import React, { Dispatch, useRef } from 'react';

import { BusinessUnit, BusinessUnitComponent } from '@zola-helpers/client/dist/es/@types';
import type { FlowEventConfig } from '@zola/tracking-contracts/src/configs';
import type { UserContext } from '@zola-helpers/client/dist/es/@types';
import type { GenericOnboardingApiCall } from '../OnboardV2/types';

import { OnboardingProvider } from '../OnboardV2/context';
import OnboardModalContainer from '../OnboardV2/OnboardModalContainer';
import OnboardSection from '../OnboardV2/OnboardSection';
import { QuickSignupStep } from '../OnboardV2/common/steps/QuickSignupStep/QuickSignupStep';

import { PaperOnboardTypes } from './constants';
import { usePaperOnboardContext } from './PaperOnboardContext';
import useSignupHelpers from '../OnboardV2/util/useSignupHelpers';

import { hidePortal } from '../OnboardV2/OnboardContainer/OnboardContainer.styles';

type QuickSignupFlowProps = {
  appDispatch: Dispatch<unknown>;
  businessUnitComponent: string;
  businessCategory: FlowEventConfig['business_category'];
  flowEntry: FlowEventConfig['flow_entry'];
  formInitialValues?: Record<string, unknown>;
  onClose: () => void;
  onLoginSuccess?: () => Promise<void>;
  onOnboardSuccessCb: () => Promise<void>;
  onOnboardingStarted?: () => void;
  rootId: string;
  userContext: UserContext;
};

export const QuickSignupFlow = ({
  appDispatch,
  businessUnitComponent,
  businessCategory,
  flowEntry,
  formInitialValues,
  onClose,
  onLoginSuccess,
  onOnboardSuccessCb,
  onOnboardingStarted,
  rootId,
  userContext,
}: QuickSignupFlowProps): JSX.Element => {
  const ref = useRef<HTMLDivElement | null>(null);
  const onSignup = useSignupHelpers({
    appDispatch,
    recaptchaContainer: ref.current,
  });
  const { paperOnboardType } = usePaperOnboardContext();

  return (
    <div ref={ref}>
      <OnboardingProvider
        flowTrackingContractsData={{
          business_category: businessCategory,
          business_unit: 'PAPER',
          flow_entry: flowEntry,
          flow_type: 'ONBOARDING',
        }}
        userContext={userContext}
      >
        <OnboardModalContainer
          businessUnit={BusinessUnit.INVITATIONS}
          businessUnitComponent={businessUnitComponent as BusinessUnitComponent}
          closeOnEscape
          closeOnOverlayClick
          css={hidePortal}
          fullScreen={false}
          lockBgScrolling
          onClose={onClose}
          onLoginSuccess={onLoginSuccess}
          onOnboardingStarted={onOnboardingStarted}
          onOnboardSuccessCb={onOnboardSuccessCb}
          rootId={rootId}
          showProgressBar={false}
          triggerRef={null}
        >
          <OnboardSection
            formInitialValues={formInitialValues}
            onFormSubmit={onSignup as unknown as GenericOnboardingApiCall}
          >
            <QuickSignupStep
              {...(paperOnboardType === PaperOnboardTypes.QUICK_SIGNUP_CHECKOUT && {
                hed: 'Create a free account to checkout',
              })}
              {...(paperOnboardType === PaperOnboardTypes.QUICK_SIGNUP && {
                dek: 'Your design is looking good. Enter a few details so you can jump back to this one (or explore others).',
                hed: "Let's save your draft",
              })}
            />
          </OnboardSection>
        </OnboardModalContainer>
      </OnboardingProvider>
    </div>
  );
};
